import { useDaily } from "@daily-co/daily-react";
import logger from "logging/logger";
import { selectCurrentBackground } from "modules/audioVideo/redux/selectors";
import { isInPresentationMode } from "modules/broadcast/broadcast.firebase";
import { selectCurrentTheaterId } from "modules/theater/selectors";
import { useMutation } from "@tanstack/react-query";
import { useAppDispatch } from "store/hooks";
import { setDailyResult } from "modules/systemCheck/redux/systemCheckSlice";
import { useSelector } from "react-redux";
import { useDailyToken } from "../useDailyToken/useDailyToken";
import { useUserData } from "../useUserData";
import { parseRequest as parseVirtualBackgroundRequest } from "../useVirtualBackground/processor";
import { JOIN_MEETING_CACHE_KEY } from "./constants";
import { getDailyMeetingUrl } from "./urls";
import { UserNotRegisteredTokenError } from "../useDailyToken/constants";

export const useJoinMeeting = () => {
  const dispatch = useAppDispatch();
  const daily = useDaily();
  const { getToken } = useDailyToken();
  const { setConversationId } = useUserData();
  const theaterId = useSelector(selectCurrentTheaterId);
  const currentBackground = useSelector(selectCurrentBackground);

  type JoinMeetingRequest = {
    eventId: string;
    conversationId: string;
  };

  const { mutateAsync: joinMeeting } = useMutation({
    mutationKey: [JOIN_MEETING_CACHE_KEY],

    mutationFn: async (payload: JoinMeetingRequest) => {
      if (!daily) {
        throw new Error("no daily call object");
      }
      if (!theaterId) {
        throw new Error("no theater Id");
      }

      const { eventId, conversationId } = payload;

      const isInPresentation = await isInPresentationMode(theaterId);

      const room = await daily.room();
      const hasJoinedMeeting = room && "name" in room && room.name === eventId;

      if (!hasJoinedMeeting || isInPresentation) {
        // joining a different event, or haven't joined a room at all (not possible atm)
        const token = await getToken({
          meetingId: eventId,
          isMicrophoneOn: false,
          isCameraOn: false,
          ignoreCache: true,
        });

        const processorConfig = await parseVirtualBackgroundRequest(
          currentBackground,
        );

        await daily.leave();
        await daily.join({
          inputSettings: {
            video: { processor: processorConfig },
          },
          token,
          url: getDailyMeetingUrl({ meetingId: eventId }),
          subscribeToTracksAutomatically: false,
          startAudioOff: false,
          startVideoOff: false,
        });
      }

      dispatch(
        setDailyResult({
          isSuccessful: true,
          isDirty: false,
          isLoading: false,
        }),
      );

      await setConversationId({ conversationId });
    },

    onError: (error) => {
      if (error instanceof UserNotRegisteredTokenError) {
        logger.error(
          "[useJoinMeeting] No token while joining a meeting, user is not registered to current event",
        );
        return;
      }

      dispatch(
        setDailyResult({
          isSuccessful: false,
          isDirty: false,
          isLoading: false,
        }),
      );

      logger.error(`[useJoinMeeting] error joining daily meeting, ${error}`);
    },
  });

  return { joinMeeting };
};
